import {TableContainer, TableBody, TableCell, TableHead, TableRow, Paper, Table, CircularProgress, Grid} from '@mui/material';
import {Chart as ChartJS, BarController, BarElement} from 'chart.js'; 
import {useTranslation} from "react-i18next";
import {Bar, Line} from "react-chartjs-2";
import React, {useState} from 'react';
import {observer} from 'mobx-react';
import {TAB_DOW, TAB_MONTH2, TAB_YEAR2, TAB_YEAR_CUMULATIVE} from './StatsObsCount.js';
import {dayNames, monthNames, monthNamesShort} from '../Trans.js';
import {useGlobalStorage} from '../mobx/GlobalStorage.js';
import {TAB_OTHER, TAB_OBS_COUNT} from './StatsCommon.js';
import StatsFilter from './StatsFilter.js';
import {COMM_OK} from '../WsR2.js';

ChartJS.register(BarController, BarElement);

export const TGT_DOW			= 0
export const TGT_MONTH			= 1
export const TGT_YEAR			= 2
export const TGT_YEAR_CUMULATIVE	= 3

const StatsDowBarGraphStats = (props) => {
	const {dataset, tgt, max_y, legend_fn} = props;
	const {t} = useTranslation('obs');

	if (!dataset || !dataset.data.length || tgt === TGT_YEAR_CUMULATIVE)
		return null;

	const tgt_str = tgt === TGT_DOW ? t('stats.dow') : 
		tgt === TGT_MONTH ? t('stats.month') : 
		tgt === TGT_YEAR ? t('stats.year') : '[unknown target]';

	return (
		<>
		<Grid size={{xs: 12}} sx={{mb: 2}} align={'center'}>
			<div style={{width: 300}}>
				<Bar
					data={{
						labels: new Array(dataset.data.length).fill(''),
						datasets: [dataset],
					}}

					options = {{
						responsive: true,
						plugins: {
							legend: {
								position: 'top',
								display: false,
							},
							title: {
								display: false,
							}
						},
						scales: {
							y: {
								min: 0,
								max: max_y
							},
							x: {
								ticks: {
									callback: function(val, index) {
										return legend_fn(index, true);
									},
									autoSkip: false,
									minRotation: 0,
									maxRotation: 90,
								}, 
							}
						},
					}}
				/>
			</div>
		</Grid>

		<Grid size={{xs: 12}} sx={{width: 300, mb: 4}} align={'center'}>
			<TableContainer component={Paper} elevation={0}>
				<Table size='small'>
					<TableHead>
						<TableRow>
							<TableCell>{tgt_str}</TableCell>
							<TableCell align='right'>{t('stats.count')}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{dataset.data.map((e, i) => {
							return (
								<TableRow key={i}>
									<TableCell>{legend_fn(i)}</TableCell>
									<TableCell align='right'>{e}</TableCell>
								</TableRow>
							)
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</Grid>
		</>
	);
}

const StatsDow = observer((props) => {
	const [loading, setLoading] = useState(false);
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');
	const tgt = props.tgt;

	function dowRxWs(status, msg) {
		const ot = gStorage.stats.obs_cnt_tab;
		let a = [];

		setLoading(false);

		if (status !== COMM_OK) {
			alert('dowRxWs: error ' + status);
			return;
		} else if (msg.result !== 'OK') {
			alert('dowRxWs: ' + msg.data.desc + '!');
			return;
		}

		if (gStorage.stats.tab !== TAB_OTHER || gStorage.stats.other_tab !== TAB_OBS_COUNT || ![TAB_DOW, TAB_MONTH2, TAB_YEAR2, TAB_YEAR_CUMULATIVE].includes(ot))
			return;

		if (tgt === TGT_DOW) {
			a = new Array(7).fill(0);
			msg.data.daydow.forEach((e) => a[e.dow - 1] += e.count);
		} else if (tgt === TGT_MONTH) {
			a = new Array(12).fill(0);
			msg.data.daydow.forEach((e) => a[e.month - 1] += e.count);
		} else if (tgt === TGT_YEAR || tgt === TGT_YEAR_CUMULATIVE) {
			let y = -1, y_cnt = 0;

			msg.data.daydow.forEach((e) => {if (e.year !== y) {y = e.year; y_cnt++;}});
			if (y_cnt) {
				let cnt = 0;

				y = -1;

				msg.data.daydow.forEach((e) => {
					if (e.year !== y) {
						if (y >= 0) {
							a.push({year: y, count: cnt});
						}

						y = e.year;
						cnt = 0;
					}

					cnt += e.count;
				});

				if (cnt) {
					a.push({year: y, count: cnt});
				}
			}
		}

		gStorage.stats.setObsList(a);
	}

	function dowLoadList() {
		const msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'day',
			op: 'dow',
			data: {
				country: gStorage.stats.country.id,
				year: tgt === TGT_YEAR_CUMULATIVE ? 0 : gStorage.stats.statsYear,
				user: gStorage.conn.user.id
			},
		}

		// console.log('dowLoadList: Tx:', msg);

		setLoading(true);
		gStorage.conn.ws.send(msg, dowRxWs);
	}

	function dowYearChange(event) {
		gStorage.stats.setStatsYear(event.target.value);
		gStorage.stats.setObsList(null);
	}

	// function dowAllChange(event, value) {
	// 	gStorage.stats.setAllUsers(!gStorage.stats.allUsers);
	// 	gStorage.stats.setObsList(null);
	// }
	
	function dowLegend(dow, table=false) {
		if (tgt === TGT_DOW) {
			const dayname = t(dayNames[dow % 7]);

			return dayname.charAt(0).toUpperCase() + dayname.slice(1);
		} else if (tgt === TGT_MONTH) {
			if (table) 
				return t(monthNamesShort[dow % 12]);

			return t(monthNames[dow % 12]);
		}

		return gStorage.stats.obsList[dow].year;
	}

	if (gStorage.stats.tab !== TAB_OTHER || gStorage.stats.other_tab !== TAB_OBS_COUNT || ![TAB_DOW, TAB_MONTH2, TAB_YEAR2, TAB_YEAR_CUMULATIVE].includes(gStorage.stats.obs_cnt_tab))
		return null;

	if ((gStorage.stats.obs_cnt_tab === TAB_DOW && tgt !== TGT_DOW) ||
	     (gStorage.stats.obs_cnt_tab === TAB_MONTH2 && tgt !== TGT_MONTH) ||
	     (gStorage.stats.obs_cnt_tab === TAB_YEAR2 && tgt !== TGT_YEAR) ||
	     (gStorage.stats.obs_cnt_tab === TAB_YEAR_CUMULATIVE && tgt !== TGT_YEAR_CUMULATIVE))
		return null;

	if (!gStorage.stats.obsList && !loading) {
		if (gStorage.conn.online)
			dowLoadList();

		return null;
	}

	if (loading) {
		return (
			<Grid container direction="column" justifyContent="center" alignItems="center" spacing={0}>
				<CircularProgress sx={{mt: 10}} />
			</Grid>
		);
	}

	if (!gStorage.stats.obsList)
		return null;

	let dataset = {
		label: '',
		data: null, 
		borderColor: 'green',
		borderWidth: 1,
		pointRadius: 2,
	};

	if (tgt === TGT_YEAR) {
		dataset.data = [];

		gStorage.stats.obsList.forEach((e) => {
			dataset.data.push(e.count);
		});
	} else if (tgt === TGT_YEAR_CUMULATIVE) {
		dataset.data = [];
		let prev_cnt = 0;

		gStorage.stats.obsList.forEach((e) => {
			dataset.data.push(e.count + prev_cnt);
			prev_cnt += e.count;
		});
	} else {
		dataset.data = gStorage.stats.obsList;
	}

	let max_val = 0;
	dataset.data.forEach((e, ind) => {
		if (e > max_val)
			max_val = e;
	});

	const tt = tgt !== TGT_YEAR_CUMULATIVE ? {
		year: {
			year: gStorage.stats.statsYear,
			onChange: dowYearChange,
			enabled: gStorage.conn.online,
			allowNone: true
		}}
		: null;

	return (
		<>
			<Grid container direction="row" justifyContent="center" alignItems="center" spacing={0}>
				<Grid size={{xs: 12}} align={'center'}>
					<StatsFilter 
						loc = {{country: {
								value: gStorage.stats.country,
								style: {width: 200}
							}
						}}
						time = {tt}
						// all_users = {{
						// 	checked: gStorage.stats.allUsers,
						// 	onChange: dowAllChange,
						// 	enabled: gStorage.conn.online
						// }}
					/>
				</Grid>

				<StatsDowBarGraphStats 
					dataset={dataset} 
					tgt={tgt}
					max_y={max_val}
					legend_fn={dowLegend}
				/>

			{dataset.data.length && tgt === TGT_YEAR_CUMULATIVE &&
			<Grid size={{xs: 12}} align={'center'}>
				<div style={{width: 300}}>
					<Line
						data={{
							labels: new Array(gStorage.stats.obsList.length).fill(''),
							datasets: [dataset],
						}}

						options = {{
							responsive: true,
							plugins: {
								legend: {
									position: 'top',
									display: false,
								},
								title: {
									display: false,
								}
							},
							scales: {
								y: {
									min: 0,
									max: max_val
								},
								x: {
									ticks: {
										callback: function(val, index) {
											return dowLegend(index);
										},
										autoSkip: false,
										minRotation: 0,
										maxRotation: 90,
									}, 
								}
							},
						}}
					/>
				</div>
			</Grid>
			}

			</Grid>
		</>
	);
});

export default StatsDow;
